import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, PageLayout } from '@sentinel/ui';
import { useUser } from '@sentinel/auth-core';
import { CredentialsList } from '../features/security/components/credentials-list';
import { ConfirmDialog } from '../features/security/components/confirm-dialog';
import { DisableMfaDialog } from '../features/security/components/disable-mfa-dialog';
import { MfaSection } from '../features/security/components/mfa-section';
import { IdpManagedCard } from '../features/security/components/idp-managed-card';
import {
  type CredentialItem,
  useCredentials,
  useDeleteCredential,
} from '../features/security/use-credentials';
import { useDisableMfa } from '../features/security/use-disable-mfa';

const subsectionStyle: React.CSSProperties = {
  display: 'grid',
  gap: '0.75rem',
};
const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  alignItems: 'center',
  justifyContent: 'space-between',
};

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function SecurityPage() {
  const { t } = useTranslation('security');
  const user = useUser();
  const credentials = useCredentials({ enabled: !user?.localAuthManagedByIdp });
  const deleteCred = useDeleteCredential();
  const disableMfa = useDisableMfa();

  const [revokeTarget, setRevokeTarget] = useState<CredentialItem | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableInlineError, setDisableInlineError] = useState<string | null>(null);
  const [topError, setTopError] = useState<string | null>(null);
  const [topSuccess, setTopSuccess] = useState<string | null>(null);

  function clearBanners() {
    setTopError(null);
    setTopSuccess(null);
  }

  async function handleConfirmRevoke() {
    if (!revokeTarget) return;
    clearBanners();
    try {
      await deleteCred.mutateAsync({ id: revokeTarget.id });
      setRevokeTarget(null);
      setTopSuccess(t('credential_revoked_success'));
    } catch (e) {
      const status = getStatus(e);
      // Step-up middleware re-throws as-is on cancel; surface a generic
      // error otherwise. The middleware's retry path resolves the request
      // before reaching this catch on success.
      if (status === 403) {
        setRevokeTarget(null);
        return;
      }
      setTopError(t('generic_error'));
    }
  }

  async function handleConfirmDisableMfa(password: string) {
    setDisableInlineError(null);
    clearBanners();
    try {
      await disableMfa.mutateAsync({ password });
      setDisableOpen(false);
      setTopSuccess(t('mfa_disabled_success'));
      // Force the session to reflect the new MFA state.
      window.location.reload();
    } catch (e) {
      const status = getStatus(e);
      if (status === 401 || status === 400) {
        setDisableInlineError(t('mfa_invalid_password'));
        return;
      }
      if (status === 403) {
        // Step-up was required and the user cancelled / failed step-up.
        // The dialog stays open so the user can retry.
        return;
      }
      setDisableInlineError(t('generic_error'));
    }
  }

  return (
    <>
      <PageLayout title={t('title')} subtitle={t('subtitle')}>
        {topError ? (
          <Alert type="error" role="alert">
            {topError}
          </Alert>
        ) : null}
        {topSuccess ? (
          <Alert type="info" role="status">
            {topSuccess}
          </Alert>
        ) : null}

        {user?.localAuthManagedByIdp ? (
          <IdpManagedCard />
        ) : (
          <>
            <MfaSection
              enabled={Boolean(user?.mfaEnabled)}
              onEnable={() => {
                window.location.assign('/mfa/setup');
              }}
              onDisable={() => {
                setDisableInlineError(null);
                setDisableOpen(true);
              }}
            />

            <section style={subsectionStyle} data-testid="credentials-section">
              <div style={sectionHeaderStyle}>
                <h2 style={{ margin: 0 }}>{t('credentials_section_title')}</h2>
                <Link
                  to="/mfa/setup"
                  style={{ fontSize: 14 }}
                  data-testid="credentials-register-link"
                >
                  {t('credentials_register_link')}
                </Link>
              </div>
              <p style={{ margin: 0, fontSize: 14, color: 'rgba(245,247,255,0.6)' }}>
                {t('credentials_section_subtitle')}
              </p>

              {credentials.isLoading ? (
                <p>{t('credentials_loading')}</p>
              ) : credentials.error ? (
                <Alert type="error" role="alert">
                  {t('generic_error')}
                </Alert>
              ) : (
                <CredentialsList
                  items={credentials.data ?? []}
                  revokingId={deleteCred.isPending ? (revokeTarget?.id ?? null) : null}
                  onRevoke={(c) => {
                    clearBanners();
                    setRevokeTarget(c);
                  }}
                />
              )}
            </section>
          </>
        )}
      </PageLayout>

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        title={t('credential_revoke_confirm_title')}
        subtitle={t('credential_revoke_confirm_subtitle')}
        submitLabel={t('credential_revoke_submit')}
        cancelLabel={t('credential_revoke_cancel')}
        submitting={deleteCred.isPending}
        variant="danger"
        onSubmit={handleConfirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />

      <DisableMfaDialog
        open={disableOpen}
        submitting={disableMfa.isPending}
        inlineError={disableInlineError}
        onSubmit={handleConfirmDisableMfa}
        onCancel={() => {
          setDisableOpen(false);
          setDisableInlineError(null);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/account/security')({
  component: SecurityPage,
});
